import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { companyHasAddon } from '../lib/companyAddons';

const CompanyAdminAuthContext = createContext(null);

export { CompanyAdminAuthContext };

const TOKEN_KEY   = 'company_admin_token';
const COMPANY_KEY = 'company_admin_company';
const USER_KEY    = 'company_admin_user';
const PERMS_KEY   = 'company_admin_permissions';
const CONFIG_KEY  = 'company_admin_config';

const API = process.env.REACT_APP_BACKEND_URL;

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readInitialPermissions() {
  const storedUser = readJson(USER_KEY);
  if (Number(storedUser?.user_type) === 1) return null; // admin = unrestricted
  const storedPerms = readJson(PERMS_KEY);
  if (storedPerms) return storedPerms;
  // Manager/agent with no cached perms: empty until fetch, not admin-all.
  if (storedUser) return {};
  return null;
}

export const CompanyAdminAuthProvider = ({ children }) => {
  const [company, setCompany] = useState(() => readJson(COMPANY_KEY));
  const [user, setUser] = useState(() => readJson(USER_KEY));
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const loading = false;
  const [userPermissions, setUserPermissions] = useState(readInitialPermissions);
  const [companyConfig, setCompanyConfig] = useState(() => readJson(CONFIG_KEY));

  // Fetch company config (ai_chat, allow_premium_plan, etc.)
  const fetchCompanyConfig = useCallback(async (authToken) => {
    if (!authToken) return;
    try {
      const res = await axios.get(
        `${API}/api/v1/admin/company/config`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const cfg = res.data || {};
      setCompanyConfig(cfg);
      localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
      if (cfg.currency) {
        setCompany((prev) => {
          if (!prev || prev.currency === cfg.currency) return prev;
          const next = { ...prev, currency: cfg.currency };
          localStorage.setItem(COMPANY_KEY, JSON.stringify(next));
          return next;
        });
      }
      return cfg;
    } catch {
      setCompanyConfig({ ai_chat: 'enabled', features: [], active_addons: [] });
    }
  }, []);

  // Fetch permissions for the current user (managers / agents only)
  const fetchPermissions = useCallback(async (userId, authToken) => {
    if (!userId || !authToken) { setUserPermissions({}); return; }
    try {
      const res = await axios.get(
        `${API}/api/v1/admin/company/users/${userId}/permissions`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const perms = res.data?.permissions || {};
      setUserPermissions(perms);
      localStorage.setItem(PERMS_KEY, JSON.stringify(perms));
    } catch {
      setUserPermissions({});
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    fetchCompanyConfig(token);
    refreshMe();
    const utype = Number(user?.user_type);
    if (utype === 1) {
      setUserPermissions(null);
    } else if (user?.user_id) {
      fetchPermissions(user.user_id, token);
    }
  }, []); // eslint-disable-line

  const login = (companyData, accessToken, userData = null) => {
    setCompany(companyData);
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(COMPANY_KEY, JSON.stringify(companyData));
    fetchCompanyConfig(accessToken);
    if (userData) localStorage.setItem(USER_KEY, JSON.stringify(userData));
    else localStorage.removeItem(USER_KEY);

    const utype = Number(userData?.user_type);
    if (utype === 1) {
      setUserPermissions(null); // admin = no restrictions
      localStorage.removeItem(PERMS_KEY);
    } else if (userData?.user_id) {
      fetchPermissions(userData.user_id, accessToken);
    } else {
      setUserPermissions({});
    }
  };

  const logout = () => {
    setCompany(null); setUser(null); setToken(null); setUserPermissions(null); setCompanyConfig(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(COMPANY_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(PERMS_KEY);
    localStorage.removeItem(CONFIG_KEY);
  };

  // Helper: null = admin (all allowed); otherwise check key
  const hasPermission = useCallback((key) => {
    if (userPermissions === null) return true; // admin
    return !!userPermissions[key]?.visible;
  }, [userPermissions]);

  // Helper: check action-level permission (add/edit/delete)
  const canAction = useCallback((key, action) => {
    if (userPermissions === null) return true; // admin — all actions allowed
    return !!userPermissions[key]?.[action];
  }, [userPermissions]);

  const hasAddon = useCallback(
    (key) => companyHasAddon(companyConfig, key),
    [companyConfig],
  );

  const refreshMe = useCallback(async () => {
    const authToken = token || localStorage.getItem(TOKEN_KEY);
    if (!authToken) return;
    try {
      const res = await axios.get(`${API}/api/v1/admin/company/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.data?.company) {
        setCompany(res.data.company);
        localStorage.setItem(COMPANY_KEY, JSON.stringify(res.data.company));
      }
      if (res.data?.user) {
        const nextUser = {
          ...res.data.user,
          user_id: res.data.user.user_id || res.data.user._id,
        };
        setUser(nextUser);
        localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
      }
    } catch {
      /* keep current session */
    }
  }, [token]);

  const refreshCompanyConfig = useCallback(
    () => (token ? fetchCompanyConfig(token) : Promise.resolve()),
    [token, fetchCompanyConfig],
  );

  const applyCompany = useCallback((companyData) => {
    if (!companyData || typeof companyData !== "object") return;
    setCompany((prev) => {
      const next = { ...(prev || {}), ...companyData };
      localStorage.setItem(COMPANY_KEY, JSON.stringify(next));
      return next;
    });
    if (companyData.currency) {
      setCompanyConfig((prev) => {
        const next = { ...(prev || {}), currency: companyData.currency };
        localStorage.setItem(CONFIG_KEY, JSON.stringify(next));
        return next;
      });
    }
  }, []);

  const refreshPermissions = useCallback(
    () =>
      user?.user_id && token
        ? fetchPermissions(user.user_id, token)
        : Promise.resolve(),
    [user?.user_id, token, fetchPermissions],
  );

  return (
    <CompanyAdminAuthContext.Provider value={{
      company, user, token, login, logout,
      isAuthenticated: !!token,
      loading,
      userPermissions,
      hasPermission,
      canAction,
      companyConfig,
      hasAddon,
      refreshMe,
      refreshUser: refreshMe,
      applyCompany,
      refreshCompanyConfig,
      refreshPermissions,
    }}>
      {children}
    </CompanyAdminAuthContext.Provider>
  );
};

export const useCompanyAdminAuth = () => {
  const ctx = useContext(CompanyAdminAuthContext);
  if (!ctx) throw new Error('useCompanyAdminAuth must be used within CompanyAdminAuthProvider');
  return ctx;
};
